import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import ApiService from '../services/api';
import {
  FileText,
  Upload,
  Users,
  TrendingUp,
  UserCheck,
  Search,
  Trophy,
  ChevronRight,
  Wheat,
  Leaf,
  Monitor,
  Droplets,
  Rocket,
  HelpCircle,
  Plus,
  Minus,
} from 'lucide-react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Stack,
  Chip,
  CircularProgress
} from '@mui/material';
import theme from '../theme';

const Nominations = () => {
  const { t } = useTranslation('nominations');
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();

  // Get current language
  const currentLang = lang || 'en';
  const [expandedFaq, setExpandedFaq] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('farmer'); // 'innovation', 'farmer', 'fpo'

  // Fetch awards from backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const backendAwards = await ApiService.getCategories(currentLang);

        // Filter only active awards and convert to frontend format
        const formattedAwards = backendAwards
          .filter(award => award.isActive === true)
          .map(award => ApiService.convertBackendCategoryToFrontend(award));

        setCategories(formattedAwards);
      } catch (error) {
        console.error('Failed to fetch awards:', error);
        // Fall back to empty array - user will see error or empty state
        setCategories([]);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [currentLang]);

  // Handle scroll to section when component loads with hash
  useEffect(() => {
    if (location.hash === '#how-to-enter' || location.hash === '#categories') {
      // Small delay to ensure the component has rendered
      setTimeout(() => {
        const elementId = location.hash.substring(1); // Remove the # symbol
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  }, [location.hash]);

  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const handleNavigation = () => {
    // Scroll to top when navigating to a new page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Map English filter values to Kannada category values
  const categoryMapping = {
    'en': {
      'farmer': 'farmer',
      'fpo': 'fpo',
      'innovation': 'innovation'
    },
    'kn': {
      'farmer': 'ರೈತ',
      'fpo': 'ಎಫ್ ಪಿ ಓ',
      'innovation': 'ನಾವೀನ್ಯತೆ'
    }
  };

  // Map English awardType values to Kannada awardType values
  const awardTypeMapping = {
    'en': {
      'district': 'district',
      'zonal': 'zonal',
      'state': 'state'
    },
    'kn': {
      'district': 'ಜಿಲ್ಲೆ',
      'zonal': 'ವಲಯ (ಝೋನಲ್)',
      'state': 'ರಾಜ್ಯ'
    }
  };

  // Get the category value for the current language
  const getCategoryForLanguage = (filterValue) => {
    const langMap = categoryMapping[currentLang] || categoryMapping['en'];
    return langMap[filterValue] || filterValue;
  };

  // Get the awardType value for the current language
  const getAwardTypeForLanguage = (awardTypeValue) => {
    const langMap = awardTypeMapping[currentLang] || awardTypeMapping['en'];
    return langMap[awardTypeValue] || awardTypeValue;
  };

  // Filter categories based on selected filter
  const filteredCategories = categories.filter(category => {
    const categoryValue = getCategoryForLanguage(selectedFilter);
    return category.category?.toLowerCase() === categoryValue.toLowerCase();
  });

  // For farmers, separate by awardType (district on left, zonal on right)
  const getColumnCategories = (columnIndex) => {
    if (selectedFilter === 'farmer') {
      // Left column (0): district awards
      // Right column (1): zonal awards
      const districtType = getAwardTypeForLanguage('district');
      const zonalType = getAwardTypeForLanguage('zonal');

      if (columnIndex === 0) {
        return filteredCategories.filter(cat => cat.awardType?.toLowerCase() === districtType.toLowerCase());
      } else {
        return filteredCategories.filter(cat => cat.awardType?.toLowerCase() === zonalType.toLowerCase());
      }
    } else {
      // For other categories, split evenly between columns
      const midpoint = Math.ceil(filteredCategories.length / 2);
      if (columnIndex === 0) {
        return filteredCategories.slice(0, midpoint);
      } else {
        return filteredCategories.slice(midpoint);
      }
    }
  };

  // Icon mapping for steps
  const stepIconMap = {
    FileText,
    Upload,
    Search,
    UserCheck,
    Trophy
  };

  // Get steps with icons
  const stepsWithIcons = [];
  for (let i = 0; i < 5; i++) {
    const step = {
      number: t(`steps.list.${i}.number`),
      title: t(`steps.list.${i}.title`),
      details: t(`steps.list.${i}.details`),
      icon: stepIconMap[['FileText', 'Upload', 'Search', 'UserCheck', 'Trophy'][i]]
    };
    stepsWithIcons.push(step);
  }

  // Icon mapping to convert string icon names to components
  const iconMap = {
    Wheat,
    Monitor,
    Leaf,
    Droplets,
    Rocket,
    UserCheck,
    Users,
    Search,
    TrendingUp,
    Trophy,
  };

  return (
    <Box sx={{ backgroundColor: '#FFFFFF' }}>
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <Box sx={{
        position: 'relative',
        pt: { xs: "160px", lg: "120px" },
        pb: { xs: "60px", lg: "80px" },
        minHeight: { xs: '50vh', md: '70vh' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        overflow: 'visible',
        backgroundImage: `url("/assets/awards_bg.png")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}>
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle, rgba(212, 165, 32, 0.5) 0%, rgba(0, 0, 0, 0.9) 100%, rgba(0, 0, 0, 0.9) 100%);',
          }}
        />

        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.light',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.875rem',
              }}
            >
              {t('hero.overline')}
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 'bold',
                mb: 3,
                lineHeight: 1.1,
                background: 'linear-gradient(135deg, #1A1A1A 27%, #D4A747 50%, #1A1A1A 73%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {t('hero.title')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                mb: 4,
                opacity: 0.9,
                maxWidth: 800,
                color: theme.palette.secondary.main,
                mx: 'auto'
              }}
            >
              {t('hero.subtitle')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                opacity: 0.8,
                maxWidth: 600,
                color: theme.palette.secondary.main,
                mx: 'auto'
              }}
            >
              {t('hero.description')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* How to Enter Section */}
      <Box id="how-to-enter" sx={{ py: { xs: 6, md: 6 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('steps.overline')}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 900,
                  mb: 2
                }}
              >
                {t('steps.sectionTitle')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
                {t('steps.subtitle')}
              </Typography>
            </Box>
          </motion.div>

          {/* Step-by-Step Procedure */}
          <Box sx={{ maxWidth: 800, mx: 'auto', position: 'relative' }}>
            {/* Clean Connecting Line */}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                left: 30,
                top: 30,
                bottom: 'calc(30px + 60px)',
                width: 2,
                background: 'linear-gradient(180deg, #D4A747 0%, #B8901C 100%)',
                borderRadius: 1,
                opacity: 0.3
              }}
            />

            {stepsWithIcons.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: { xs: 6, md: 7 },
                    position: 'relative',
                    '&:last-child': { mb: 0 }
                  }}
                >
                  {/* Clean Step Number */}
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 2,
                      width: { xs: 50, md: 60 },
                      height: { xs: 50, md: 60 },
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #D4A747 0%, #D4A520 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: { xs: '1.1rem', md: '1.3rem' },
                      mr: { xs: 3, md: 4 },
                      flexShrink: 0,
                      border: '3px solid white',
                      boxShadow: '0 4px 16px rgba(212, 167, 71, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 6px 20px rgba(212, 167, 71, 0.3)'
                      }
                    }}
                  >
                    {step.number}

                    {/* Mobile connecting line */}
                    {index < stepsWithIcons.length - 1 && (
                      <Box
                        sx={{
                          display: { xs: 'block', md: 'none' },
                          position: 'absolute',
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 2,
                          height: 50,
                          background: '#D4A747',
                          opacity: 0.3
                        }}
                      />
                    )}
                  </Box>

                  {/* Step Content */}
                  <Box
                    sx={{
                      flex: 1,
                      pt: 0
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: '16px',
                        background: 'white',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                        }
                      }}
                    >
                      <Box sx={{ p: 2.5, mt: -1, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '8px',
                              background: 'rgba(212, 167, 71, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#D4A747',
                              mr: 1.5,
                              flexShrink: 0
                            }}
                          >
                            <step.icon size={18} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: '#B8901C',
                                mb: 0.5,
                                lineHeight: 1.3,
                                fontSize: '1.1rem'
                              }}
                            >
                              {step.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                lineHeight: 1.4,
                                fontSize: '0.8rem'
                              }}
                            >
                              {step.details}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            ))}
          </Box>

          {/* Call to Action */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Button
                variant="contained"
                size="large"
                endIcon={<ChevronRight size={18} />}
                onClick={() => {
                  handleNavigation();
                  const categoriesSection = document.getElementById('categories');
                  if (categoriesSection) {
                    categoriesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #D4A520 100%)',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: '12px',
                  textTransform: 'none',
                  boxShadow: '0 4px 16px rgba(212, 167, 71, 0.3)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(212, 167, 71, 0.4)'
                  }
                }}
              >
                {t('buttons.startYourApplication')}
              </Button>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* Filter Buttons - Moved before Outreach Section */}
      <Box sx={{ pt: 8, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant={selectedFilter === 'farmer' ? 'contained' : 'outlined'}
                  onClick={() => setSelectedFilter('farmer')}
                  startIcon={<Wheat size={18} />}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: '24px',
                    fontWeight: 600,
                    textTransform: 'none',
                    ...(selectedFilter === 'farmer' ? {
                      background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(212, 167, 71, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                        boxShadow: '0 6px 16px rgba(212, 167, 71, 0.4)'
                      }
                    } : {
                      borderColor: '#D4A747',
                      color: '#D4A747',
                      '&:hover': {
                        borderColor: '#B8901C',
                        backgroundColor: 'rgba(212, 167, 71, 0.05)'
                      }
                    })
                  }}
                >
                  {t('filters.farmers')}
                </Button>
                <Button
                  variant={selectedFilter === 'fpo' ? 'contained' : 'outlined'}
                  onClick={() => setSelectedFilter('fpo')}
                  startIcon={<Users size={18} />}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: '24px',
                    fontWeight: 600,
                    textTransform: 'none',
                    ...(selectedFilter === 'fpo' ? {
                      background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(212, 167, 71, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                        boxShadow: '0 6px 16px rgba(212, 167, 71, 0.4)'
                      }
                    } : {
                      borderColor: '#D4A747',
                      color: '#D4A747',
                      '&:hover': {
                        borderColor: '#B8901C',
                        backgroundColor: 'rgba(212, 167, 71, 0.05)'
                      }
                    })
                  }}
                >
                  {t('filters.fpo')}
                </Button>
                <Button
                  variant={selectedFilter === 'innovation' ? 'contained' : 'outlined'}
                  onClick={() => setSelectedFilter('innovation')}
                  startIcon={<Rocket size={18} />}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: '24px',
                    fontWeight: 600,
                    textTransform: 'none',
                    ...(selectedFilter === 'innovation' ? {
                      background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(212, 167, 71, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                        boxShadow: '0 6px 16px rgba(212, 167, 71, 0.4)'
                      }
                    } : {
                      borderColor: '#D4A747',
                      color: '#D4A747',
                      '&:hover': {
                        borderColor: '#B8901C',
                        backgroundColor: 'rgba(212, 167, 71, 0.05)'
                      }
                    })
                  }}
                >
                  {t('filters.innovators')}
                </Button>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Categories Section */}
      <Box id="categories" sx={{ pt: 4, pb: 8, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('categories.overline')}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #D4A520 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 900,
                  mb: 2
                }}
              >
                {t('categories.sectionTitle')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 700, mx: 'auto' }}>
                {t('categories.subtitle')}
              </Typography>
            </Box>
          </motion.div>

          {isLoadingCategories ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress size={40} sx={{ color: '#D4A747' }} />
              <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                {t('categories.loadingMessage')}
              </Typography>
            </Box>
          ) : categories.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                {t('categories.emptyMessage')}
              </Typography>
            </Box>
          ) : filteredCategories.length <= 3 ? (
            // Single row layout for 3 or fewer cards
            <Grid container spacing={3} sx={{ maxWidth: 1200, mx: 'auto' }} justifyContent="center">
              {filteredCategories.map((category, index) => {
                const IconComponent = iconMap[category.icon] || Trophy;
                const isEven = index % 2 === 0;
                const primaryColor = isEven ? '#D4A747' : '#D4A520';
                const lightColor = isEven ? 'rgba(212, 167, 71, 0.1)' : 'rgba(212, 165, 32, 0.1)';

                return (
                  <Grid size={{ xs: 12, md: 4 }} key={index} sx={{ display: 'flex' }}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      style={{ width: '100%', display: 'flex' }}
                    >
                      <Card
                        sx={{
                          borderRadius: '10px',
                          border: `2px solid ${lightColor}`,
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                          transition: 'all 0.3s ease',
                          background: `linear-gradient(135deg, ${lightColor} 0%, rgba(255, 255, 255, 0.8) 100%)`,
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          width: '100%',
                          '&:hover': {
                            borderColor: primaryColor,
                            boxShadow: `0 8px 24px ${primaryColor}20`,
                            transform: 'translateY(-2px)',
                            '& .category-icon': {
                              background: primaryColor,
                              color: '#FFFFFF',
                              transform: 'scale(1.05)'
                            }
                          }
                        }}
                      >
                        <CardContent sx={{ p: { xs: 2.5, md: 3 }, position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>
                          {category.isNewAward && (
                            <Chip
                              label="NEW"
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 12,
                                left: 12,
                                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.6rem',
                                height: 20,
                                minWidth: 0,
                                zIndex: 2,
                                '& .MuiChip-label': {
                                  px: 0.75,
                                  fontSize: '0.6rem',
                                  fontWeight: 600
                                },
                                boxShadow: '0 2px 6px rgba(255, 107, 107, 0.4)',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                  '0%': { boxShadow: '0 2px 6px rgba(255, 107, 107, 0.4)' },
                                  '50%': { boxShadow: '0 4px 12px rgba(255, 107, 107, 0.6)' },
                                  '100%': { boxShadow: '0 2px 6px rgba(255, 107, 107, 0.4)' }
                                }
                              }}
                            />
                          )}

                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1, minHeight: 90 }}>
                            <Box
                              className="category-icon"
                              sx={{
                                width: { xs: 44, md: 48 },
                                height: { xs: 44, md: 48 },
                                borderRadius: '12px',
                                background: lightColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: category.color,
                                transition: 'all 0.3s ease',
                                border: `1px solid ${category.color}20`,
                                flexShrink: 0,
                              }}
                            >
                              <IconComponent size={22} strokeWidth={1.8} sx={{ color: primaryColor, '&:hover': { color: 'white' } }} />
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: '#B8901C',
                                  lineHeight: 1.3,
                                  mb: 0.5
                                }}
                              >
                                {category.title}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', mb: 1 }}>
                                {category.category && (
                                  <Chip
                                    label={category.category.charAt(0).toUpperCase() + category.category.slice(1)}
                                    size="small"
                                    sx={{
                                      background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}25 100%)`,
                                      color: primaryColor,
                                      border: `1px solid ${primaryColor}30`,
                                      fontWeight: 500,
                                      fontSize: '0.6rem',
                                      height: 18,
                                      minWidth: 0,
                                      '& .MuiChip-label': {
                                        px: 0.5,
                                        fontSize: '0.6rem',
                                        fontWeight: 500
                                      }
                                    }}
                                  />
                                )}
                                {category.awardType && (
                                  <Chip
                                    label={category.awardType}
                                    size="small"
                                    sx={{
                                      background: `${primaryColor}15`,
                                      color: primaryColor,
                                      border: `1px solid ${primaryColor}30`,
                                      fontWeight: 600,
                                      fontSize: '0.6rem',
                                      height: 18,
                                      minWidth: 0,
                                      '& .MuiChip-label': {
                                        px: 0.5,
                                        fontSize: '0.6rem',
                                        fontWeight: 600
                                      }
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              lineHeight: 1.4,
                              mt: 1.5,
                              flex: 1
                            }}
                          >
                            {category.description}
                          </Typography>

                          <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid rgba(212, 167, 71, 0.1)' }}>
                            <Button
                              fullWidth
                              variant="contained"
                              size="small"
                              endIcon={<ChevronRight size={16} />}
                              onClick={() => navigate(`/${currentLang}/apply?role=${selectedFilter}&category=${category._id}&awardType=${category.awardType || ''}`)}
                              sx={{
                                background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                                color: 'white',
                                py: 1,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                borderRadius: '8px',
                                textTransform: 'none',
                                boxShadow: `0 2px 8px ${primaryColor}40`,
                                '&:hover': {
                                  background: `linear-gradient(135deg, ${primaryColor}dd 0%, ${primaryColor} 100%)`,
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 4px 12px ${primaryColor}50`
                                }
                              }}
                            >
                              {t('buttons.applyNow')}
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Grid container spacing={4} sx={{ maxWidth: 1200, mx: 'auto' }}>
              {/* First Column - District awards for farmers, or first half for others */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Stack spacing={3}>
                  {getColumnCategories(0).map((category, index) => {
                    const IconComponent = iconMap[category.icon] || Trophy;
                    // Alternate colors: even indices get green, odd get gold
                    const isEven = index % 2 === 0;
                    const primaryColor = isEven ? '#D4A747' : '#D4A520';
                    const lightColor = isEven ? 'rgba(212, 167, 71, 0.1)' : 'rgba(212, 165, 32, 0.1)';

                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.03 }}
                        viewport={{ once: true }}
                      >
                        <Card
                          sx={{
                            borderRadius: '10px',
                            border: `2px solid ${lightColor}`,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                            transition: 'all 0.3s ease',
                            background: `linear-gradient(135deg, ${lightColor} 0%, rgba(255, 255, 255, 0.8) 100%)`,
                            '&:hover': {
                              borderColor: primaryColor,
                              boxShadow: `0 8px 24px ${primaryColor}20`,
                              transform: 'translateY(-2px)',
                              '& .category-icon': {
                                background: primaryColor,
                                color: '#FFFFFF',
                                transform: 'scale(1.05)'
                              }
                            }
                          }}
                        >
                          <CardContent sx={{ p: { xs: 2.5, md: 3 }, position: 'relative' }}>
                            {/* NEW Chip - Top Left */}
                            {category.isNewAward && (
                              <Chip
                                label="NEW"
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 12,
                                  left: 12,
                                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.6rem',
                                  height: 20,
                                  minWidth: 0,
                                  zIndex: 2,
                                  '& .MuiChip-label': {
                                    px: 0.75,
                                    fontSize: '0.6rem',
                                    fontWeight: 600
                                  },
                                  boxShadow: '0 2px 6px rgba(255, 107, 107, 0.4)',
                                  animation: 'pulse 2s infinite',
                                  '@keyframes pulse': {
                                    '0%': {
                                      boxShadow: '0 2px 6px rgba(255, 107, 107, 0.4)'
                                    },
                                    '50%': {
                                      boxShadow: '0 4px 12px rgba(255, 107, 107, 0.6)'
                                    },
                                    '100%': {
                                      boxShadow: '0 2px 6px rgba(255, 107, 107, 0.4)'
                                    }
                                  }
                                }}
                              />
                            )}

                            {/* Header with Icon, Title, and Apply Button */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                              <Box
                                className="category-icon"
                                sx={{
                                  width: { xs: 44, md: 48 },
                                  height: { xs: 44, md: 48 },
                                  borderRadius: '12px',
                                  background: lightColor,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: category.color,
                                  transition: 'all 0.3s ease',
                                  border: `1px solid ${category.color}20`,
                                  flexShrink: 0,
                                }}
                              >
                                <IconComponent size={22} strokeWidth={1.8} sx={{ color: primaryColor, '&:hover': { color: 'white' } }} />
                              </Box>

                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#B8901C',
                                    lineHeight: 1.3,
                                    mb: 0.5
                                  }}
                                >
                                  {category.title}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', mb: 1 }}>
                                  {category.category && (
                                    <Chip
                                      label={category.category.charAt(0).toUpperCase() + category.category.slice(1)}
                                      size="small"
                                      sx={{
                                        background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}25 100%)`,
                                        color: primaryColor,
                                        border: `1px solid ${primaryColor}30`,
                                        fontWeight: 500,
                                        fontSize: '0.6rem',
                                        height: 18,
                                        minWidth: 0,
                                        '& .MuiChip-label': {
                                          px: 0.5,
                                          fontSize: '0.6rem',
                                          fontWeight: 500
                                        },
                                        '&:hover': {
                                          background: `linear-gradient(135deg, ${primaryColor}25 0%, ${primaryColor}35 100%)`,
                                          transform: 'translateY(-1px)',
                                          boxShadow: `0 1px 3px ${primaryColor}20`
                                        }
                                      }}
                                    />
                                  )}
                                  {category.awardType && (
                                    <Chip
                                      label={category.awardType}
                                      size="small"
                                      sx={{
                                        background: `${primaryColor}15`,
                                        color: primaryColor,
                                        border: `1px solid ${primaryColor}30`,
                                        fontWeight: 600,
                                        fontSize: '0.6rem',
                                        height: 18,
                                        minWidth: 0,
                                        '& .MuiChip-label': {
                                          px: 0.5,
                                          fontSize: '0.6rem',
                                          fontWeight: 600
                                        }
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>

                              {/* Apply Button - Visible on large screens */}
                              <Box sx={{ display: { xs: 'none', md: 'block' }, flexShrink: 0 }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  endIcon={<ChevronRight size={14} />}
                                  onClick={() => navigate(`/${currentLang}/apply?role=${selectedFilter}&category=${category._id}&awardType=${category.awardType || ''}`)}
                                  sx={{
                                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                                    color: 'white',
                                    px: 2,
                                    py: 0.75,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    boxShadow: `0 2px 6px ${primaryColor}30`,
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                      background: `linear-gradient(135deg, ${primaryColor}dd 0%, ${primaryColor} 100%)`,
                                      transform: 'translateY(-1px)',
                                      boxShadow: `0 3px 10px ${primaryColor}40`
                                    }
                                  }}
                                >
                                  {t('buttons.apply')}
                                </Button>
                              </Box>
                            </Box>

                            {/* Description - Full width */}
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                lineHeight: 1.4,
                                mt: 1.5
                              }}
                            >
                              {category.description}
                            </Typography>

                            {/* Apply Button - Visible on small screens */}
                            <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2.5, pt: 2, borderTop: '1px solid rgba(212, 167, 71, 0.1)' }}>
                              <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                endIcon={<ChevronRight size={16} />}
                                onClick={() => navigate(`/apply?role=${selectedFilter}&category=${category._id}&awardType=${category.awardType || ''}`)}
                                sx={{
                                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                                  color: 'white',
                                  py: 1,
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  textTransform: 'none',
                                  boxShadow: `0 2px 8px ${primaryColor}40`,
                                  '&:hover': {
                                    background: `linear-gradient(135deg, ${primaryColor}dd 0%, ${primaryColor} 100%)`,
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${primaryColor}50`
                                  }
                                }}
                              >
                                {t('buttons.applyNow')}
                              </Button>
                            </Box>

                          </CardContent>
                        </Card>
                      </motion.div>
                    )
                  })}
                </Stack>
              </Grid>

              {/* Second Column - Zonal awards for farmers, or second half for others */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Stack spacing={3}>
                  {getColumnCategories(1).map((category, index) => {
                    const IconComponent = iconMap[category.icon] || Trophy;
                    // Continue alternating pattern from first column
                    const actualIndex = index + getColumnCategories(0).length;
                    const isEven = actualIndex % 2 === 0;
                    const primaryColor = isEven ? '#D4A747' : '#D4A520';
                    const lightColor = isEven ? 'rgba(212, 167, 71, 0.1)' : 'rgba(212, 165, 32, 0.1)';

                    return (
                      <motion.div
                        key={category.title + index}
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.03 }}
                        viewport={{ once: true }}
                      >
                        <Card
                          sx={{
                            borderRadius: '10px',
                            border: `2px solid ${lightColor}`,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                            transition: 'all 0.3s ease',
                            background: `linear-gradient(135deg, ${lightColor} 0%, rgba(255, 255, 255, 0.8) 100%)`,
                            '&:hover': {
                              borderColor: primaryColor,
                              boxShadow: `0 8px 24px ${primaryColor}20`,
                              transform: 'translateY(-2px)',
                              '& .category-icon': {
                                background: primaryColor,
                                color: '#FFFFFF',
                                transform: 'scale(1.05)'
                              }
                            }
                          }}
                        >
                          <CardContent sx={{ p: { xs: 2.5, md: 3 }, position: 'relative' }}>
                            {/* NEW Chip - Top Left */}
                            {category.isNewAward && (
                              <Chip
                                label="NEW"
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 12,
                                  left: 12,
                                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.6rem',
                                  height: 20,
                                  minWidth: 0,
                                  zIndex: 2,
                                  '& .MuiChip-label': {
                                    px: 0.75,
                                    fontSize: '0.6rem',
                                    fontWeight: 600
                                  },
                                  boxShadow: '0 2px 6px rgba(255, 107, 107, 0.4)',
                                  animation: 'pulse 2s infinite',
                                  '@keyframes pulse': {
                                    '0%': {
                                      boxShadow: '0 2px 6px rgba(255, 107, 107, 0.4)'
                                    },
                                    '50%': {
                                      boxShadow: '0 4px 12px rgba(255, 107, 107, 0.6)'
                                    },
                                    '100%': {
                                      boxShadow: '0 2px 6px rgba(255, 107, 107, 0.4)'
                                    }
                                  }
                                }}
                              />
                            )}

                            {/* Header with Icon, Title, and Apply Button */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                              <Box
                                className="category-icon"
                                sx={{
                                  width: { xs: 44, md: 48 },
                                  height: { xs: 44, md: 48 },
                                  borderRadius: '12px',
                                  background: lightColor,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: category.color,
                                  transition: 'all 0.3s ease',
                                  border: `1px solid ${category.color}20`,
                                  flexShrink: 0,
                                  '&:hover': {
                                    color: 'white'
                                  }
                                }}
                              >
                                <IconComponent size={22} strokeWidth={1.8} sx={{ color: primaryColor, '&:hover': { color: 'white' } }} />
                              </Box>

                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#B8901C',
                                    lineHeight: 1.3,
                                    mb: 0.5
                                  }}
                                >
                                  {category.title}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', mb: 1 }}>
                                  {category.category && (
                                    <Chip
                                      label={category.category.charAt(0).toUpperCase() + category.category.slice(1)}
                                      size="small"
                                      sx={{
                                        background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}25 100%)`,
                                        color: primaryColor,
                                        border: `1px solid ${primaryColor}30`,
                                        fontWeight: 500,
                                        fontSize: '0.6rem',
                                        height: 18,
                                        minWidth: 0,
                                        '& .MuiChip-label': {
                                          px: 0.5,
                                          fontSize: '0.6rem',
                                          fontWeight: 500
                                        },
                                        '&:hover': {
                                          background: `linear-gradient(135deg, ${primaryColor}25 0%, ${primaryColor}35 100%)`,
                                          transform: 'translateY(-1px)',
                                          boxShadow: `0 1px 3px ${primaryColor}20`
                                        }
                                      }}
                                    />
                                  )}
                                  {category.awardType && (
                                    <Chip
                                      label={category.awardType}
                                      size="small"
                                      sx={{
                                        background: `${primaryColor}15`,
                                        color: primaryColor,
                                        border: `1px solid ${primaryColor}30`,
                                        fontWeight: 600,
                                        fontSize: '0.6rem',
                                        height: 18,
                                        minWidth: 0,
                                        '& .MuiChip-label': {
                                          px: 0.5,
                                          fontSize: '0.6rem',
                                          fontWeight: 600
                                        }
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>

                              {/* Apply Button - Visible on large screens */}
                              <Box sx={{ display: { xs: 'none', md: 'block' }, flexShrink: 0 }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  endIcon={<ChevronRight size={14} />}
                                  onClick={() => navigate(`/${currentLang}/apply?role=${selectedFilter}&category=${category._id}&awardType=${category.awardType || ''}`)}
                                  sx={{
                                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                                    color: 'white',
                                    px: 2,
                                    py: 0.75,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    boxShadow: `0 2px 6px ${primaryColor}30`,
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                      background: `linear-gradient(135deg, ${primaryColor}dd 0%, ${primaryColor} 100%)`,
                                      transform: 'translateY(-1px)',
                                      boxShadow: `0 3px 10px ${primaryColor}40`
                                    }
                                  }}
                                >
                                  {t('buttons.apply')}
                                </Button>
                              </Box>
                            </Box>

                            {/* Description - Full width */}
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                lineHeight: 1.4,
                                mt: 1.5
                              }}
                            >
                              {category.description}
                            </Typography>

                            {/* Apply Button - Visible on small screens */}
                            <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2.5, pt: 2, borderTop: '1px solid rgba(212, 167, 71, 0.1)' }}>
                              <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                endIcon={<ChevronRight size={16} />}
                                onClick={() => navigate(`/apply?role=${selectedFilter}&category=${category._id}&awardType=${category.awardType || ''}`)}
                                sx={{
                                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                                  color: 'white',
                                  py: 1,
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  textTransform: 'none',
                                  boxShadow: `0 2px 8px ${primaryColor}40`,
                                  '&:hover': {
                                    background: `linear-gradient(135deg, ${primaryColor}dd 0%, ${primaryColor} 100%)`,
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${primaryColor}50`
                                  }
                                }}
                              >
                                {t('buttons.applyNow')}
                              </Button>
                            </Box>

                          </CardContent>
                        </Card>
                      </motion.div>
                    )
                  })}
                </Stack>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('faq.overline')}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #D4A520 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 900,
                  mb: 2
                }}
              >
                {t('faq.sectionTitle')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
                {t('faq.subtitle')}
              </Typography>
            </Box>
          </motion.div>

          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Accordion
                  expanded={expandedFaq === `panel${index}`}
                  onChange={handleFaqChange(`panel${index}`)}
                  sx={{
                    mb: 2,
                    borderRadius: '16px !important',
                    border: 'none',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      margin: '0 0 16px 0',
                      boxShadow: '0 8px 32px rgba(212, 167, 71, 0.15)'
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <IconButton
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: expandedFaq === `panel${index}`
                            ? 'linear-gradient(135deg, #D4A747 0%, #D4A520 100%)'
                            : 'rgba(212, 167, 71, 0.1)',
                          color: expandedFaq === `panel${index}` ? 'white' : '#D4A747',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #D4A747 0%, #D4A520 100%)',
                            color: 'white'
                          }
                        }}
                      >
                        {expandedFaq === `panel${index}` ? <Minus size={16} /> : <Plus size={16} />}
                      </IconButton>
                    }
                    sx={{
                      p: { xs: 2, md: 3 },
                      borderRadius: '16px',
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        alignItems: 'center'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <HelpCircle
                        size={20}
                        style={{
                          color: '#D4A747',
                          marginRight: '12px',
                          flexShrink: 0
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#B8901C',
                          lineHeight: 1.4
                        }}
                      >
                        {t(`faq.questions.${index}.question`)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      px: 3,
                      pb: 3,
                      pt: 0
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        pl: 4
                      }}
                    >
                      {t(`faq.questions.${index}.answer`)}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Nominations;
